/* @flow */
import { observable, computed, decorate, flow, action } from "mobx";
import { fromPromise } from "mobx-utils";
import auth0 from "auth0-js";
import { serializable } from "serializr";
import { persist } from "mobx-persist";
import config from "../config";
import * as store from "../Store";
import { APP_CALLBACK_URL, isApp } from "../helpers/nativeApp";

// Phone app: remember the sign-in between launches. In a browser the sign-in
// is restored from the Auth0 cookie; the app can't always reach that cookie
// (Google sign-in happens in a separate secure browser panel).
const APP_SESSION_KEY = "gymradio_app_session";
const saveAppSession = (accessToken, idToken, expiresAt, idTokenPayload) => {
  if (!isApp()) return;
  try {
    window.localStorage.setItem(
      APP_SESSION_KEY,
      JSON.stringify({ accessToken, idToken, expiresAt, idTokenPayload })
    );
  } catch (e) {}
};
const clearAppSession = () => {
  try {
    window.localStorage.removeItem(APP_SESSION_KEY);
  } catch (e) {}
};
const restoreAppSession = () => {
  if (!isApp()) return null;
  try {
    const s = JSON.parse(window.localStorage.getItem(APP_SESSION_KEY) || "null");
    if (!s || !s.accessToken || !s.idTokenPayload) return null;
    const left = (s.expiresAt || 0) - Date.now();
    if (left < 60 * 1000) return null;
    return {
      accessToken: s.accessToken,
      idToken: s.idToken || "",
      expiresIn: left / 1000,
      idTokenPayload: s.idTokenPayload,
    };
  } catch (e) {
    return null;
  }
};

const AUTH0_NAMESPACE = "https://gymradio.com/";

const ExplicitFilterEnum = {
  clean: "clean",
  soft: "soft",
  explicit: "explicit",
};

type ExplicitFilter = $Keys<typeof ExplicitFilterEnum>;

const DEFAULT_EXPLICIT_FILTER: ExplicitFilter = "explicit";

// stateCreds is a cookie shared across gymradio.com that nothing in the current
// player or website sets. Browsers that still hold an old one must not be
// locked out by it, so it's only a fallback and is removed once used.
const readLeftoverStateCreds = () => {
  try {
    const entry = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith("stateCreds="));
    if (!entry) return null;
    const value = JSON.parse(
      decodeURIComponent(entry.slice("stateCreds=".length))
    );
    return value && value.state ? value : null;
  } catch (e) {
    return null;
  }
};

const clearLeftoverStateCreds = () => {
  const expired = "stateCreds=; Max-Age=0; path=/";
  document.cookie = expired;
  document.cookie = `${expired}; domain=gymradio.com`;
};

export default class UserStore {
  tokenRenewalTimeout;
  store;
  franchiseStore;

  auth0 = new auth0.WebAuth({
    domain: config.AUTH0_CUSTOM_DOMAIN,
    clientID: config.AUTH0_CLIENT_ID,
    redirectUri: config.AUTH0_CALLBACK_URL,
    configurationBaseUrl: `https://${config.AUTH0_DOMAIN}`,
    audience: `https://${config.AUTH0_DOMAIN}/api/v2/`,
    responseType: "token id_token",
    scope:
      "openid profile email user_id id read:current_user update:current_user_metadata delete:current_user",
  });

  // Auth
  accessToken = "";
  idToken = "";
  expiresAt = 0;
  redirectUrl = "/";

  // User
  id = "";
  gymName = "";
  name = "";
  email = "";
  gaiaToken = "";
  franchiseId = "";

  // User state
  isLoading = false;
  isLoggedIn = false;
  isEmailVerified = false;
  isSubscribed = false;
  premiumUntil = 0;
  isBlocked = false;
  blockReason = "";
  isTrial = false;
  limitFreeStation = false;
  explicitFilter: ExplicitFilter = DEFAULT_EXPLICIT_FILTER;
  disableMotivationQuotes = false;
  // Newest login wins: true once another device has signed in to this account.
  replacedElsewhere = false;

  constructor(franchiseStore) {
    this.store = store;
    this.franchiseStore = franchiseStore;
  }

  forcePaid = action(function () {
    const renewInterval = 60 * 1000;
    this.isSubscribed = true;
    this.isTrial = false;

    setTimeout(() => {
      this.renewToken();
    }, renewInterval);
  });

  login = flow(function* (
    redirectUrl: string,
    shouldSignUp: boolean = false,
    franchiseId: string = ""
  ) {
    if (redirectUrl) {
      this.redirectUrl = redirectUrl;
    }
    let config = {
      initial_screen: shouldSignUp ? "signUp" : "login",
      mode: shouldSignUp ? "signUp" : "login",
      screen_hint: shouldSignUp ? "signUp" : undefined,
      // Always show the sign-in form, so after logging out you can sign in
      // as a different account instead of being let straight back in.
      prompt: "login",
    };
    if (franchiseId) {
      config.franchise_id = franchiseId;
    }

    this.auth0.authorize(config);
  });

  // Phone app only: Google sign-in has to happen in a secure browser panel
  // (Google blocks sign-in inside apps). The app opens this authorize page in
  // that panel and brings the result back to /callback in the app.
  loginWithGoogleInApp() {
    this.auth0.authorize({
      connection: "google-oauth2",
      redirectUri: APP_CALLBACK_URL,
    });
  }

  signUp = flow(function* (
    redirectUrl: string,
    shouldSignUp: boolean = false,
    franchiseId: string = ""
  ) {
    if (redirectUrl) {
      this.redirectUrl = redirectUrl;
    }
    let config = {
      screen_hint: "signup",
    };
    if (franchiseId) {
      config.franchise_id = franchiseId;
    }

    this.auth0.authorize(config);
  });

  setEnableMotivationQuotes = flow(function* (enabled: boolean) {
    const auth0Manage = new auth0.Management({
      domain: config.AUTH0_CUSTOM_DOMAIN,
      token: this.accessToken,
    });
    yield new Promise((resolve, reject) => {
      auth0Manage.patchUserMetadata(
        this.id,
        {
          disable_motivation_quotes: !enabled,
        },
        (err) => {
          if (err) {
            return reject(err);
          }
          resolve();
        }
      );
    });
    this.disableMotivationQuotes = !enabled;
  });

  setExplicitFilter = flow(function* (explicitFilter: ExplicitFilter) {
    const isValueValid = !!ExplicitFilterEnum[explicitFilter];
    if (!isValueValid) {
      this.explicitFilter = DEFAULT_EXPLICIT_FILTER;
      return;
    }
    const auth0Manage = new auth0.Management({
      domain: config.AUTH0_CUSTOM_DOMAIN,
      token: this.accessToken,
    });
    yield new Promise((resolve, reject) => {
      auth0Manage.patchUserMetadata(
        this.id,
        {
          explicit_filter: explicitFilter,
        },
        (err) => {
          if (err) {
            return reject(err);
          }
          resolve();
        }
      );
    });
    this.explicitFilter = explicitFilter;
  });

  reset = action(function () {
    clearAppSession();
    this.id = "";
    this.explicitFilter = DEFAULT_EXPLICIT_FILTER;
    this.gymName = "";
    this.name = "";
    this.gaiaToken = "";
    this.isLoggedIn = false;
    this.franchiseId = "";
    this.email = "";
    this.isEmailVerified = false;
    this.accessToken = "";
    this.premiumUntil = 0;
    this.expiresAt = 0;
    this.isSubscribed = false;
    this.isTrial = true;
    this.isBlocked = false;
    this.limitFreeStation = false;
    this.blockReason = "";
    this.disableMotivationQuotes = false;
    this.replacedElsewhere = false;
  });

  // ---- Newest login wins ----
  // Each real sign-in gets a login id (set by the Auth0 login Action and stored
  // on the account). This device remembers the id it signed in with; if the
  // account later shows a different id, another device has signed in since.
  deviceLoginKey(userId: string) {
    return `gr_login_id_${userId}`;
  }

  getDeviceLoginId(userId: string) {
    try {
      return window.localStorage.getItem(this.deviceLoginKey(userId)) || "";
    } catch (e) {
      return "";
    }
  }

  setDeviceLoginId(userId: string, loginId: string) {
    try {
      window.localStorage.setItem(this.deviceLoginKey(userId), loginId);
    } catch (e) {}
  }

  checkActiveLogin = flow(function* () {
    if (!this.isLoggedIn || !this.accessToken || !this.id || this.replacedElsewhere) {
      return;
    }
    const auth0Manage = new auth0.Management({
      domain: config.AUTH0_CUSTOM_DOMAIN,
      token: this.accessToken,
    });
    const userId = this.id;
    let profile;
    try {
      profile = yield new Promise((resolve, reject) => {
        auth0Manage.getUser(userId, (err, user) => (err ? reject(err) : resolve(user)));
      });
    } catch (err) {
      return; // network hiccup: never stop the music because of a failed check
    }
    const activeLoginId = ((profile && profile.app_metadata) || {}).active_login_id || "";
    if (!activeLoginId) {
      return;
    }
    const deviceLoginId = this.getDeviceLoginId(userId);
    if (!deviceLoginId) {
      // Signed in before this feature existed: adopt the current login.
      this.setDeviceLoginId(userId, activeLoginId);
      return;
    }
    if (deviceLoginId !== activeLoginId) {
      this.replacedElsewhere = true;
    }
  });

  // Delete my account (App Store and Google Play require it). The signed-in
  // user removes their own Auth0 user with the delete:current_user scope; the
  // Logins that signed in before this scope existed get a "scope" error: they
  // sign out and in again, then it works.
  async deleteAccount() {
    if (!this.accessToken || !this.id) throw new Error("Please sign in again, then try once more.");
    const url = `https://${config.AUTH0_DOMAIN}/api/v2/users/${encodeURIComponent(this.id)}`;
    let res;
    try {
      res = await fetch(url, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${this.accessToken}` },
      });
    } catch (e) {
      throw new Error("No connection. Check the internet and try again.");
    }
    if (res.status === 401 || res.status === 403) {
      throw new Error("Please sign out, sign in again and try once more.");
    }
    if (!res.ok && res.status !== 404) {
      throw new Error(`Something went wrong (${res.status}). Email support@gymradio.com and we'll delete it for you.`);
    }
  }

  logout = action(function () {
    this.reset();

    clearTimeout(this.tokenRenewalTimeout);
  });

  handleAuthentication = flow(function* () {
    return new Promise((resolve, reject) => {
      // Keep the reply from Auth0 so it can be read a second time if needed.
      const hash = window.location.hash;
      const leftover = readLeftoverStateCreds();

      const finish = (err, authResult) => {
        clearLeftoverStateCreds();
        if (authResult && authResult.accessToken && authResult.idToken) {
          this.setSession(authResult, true);
          resolve(authResult);
        } else if (err) {
          console.error(
            `Error: ${err.error}. Check the console for further details.`
          );
          reject(err);
        }
      };

      // Normal case: the login was started by this player, and auth0-js checks
      // it against what it saved. Never check it against a leftover stateCreds
      // cookie first: a stale one rejects every successful login, so the
      // customer lands back on the sign-in page in a loop.
      this.auth0.parseHash({ hash }, (err, authResult) => {
        if (err && leftover) {
          // Only for a login started somewhere else that left its details in
          // the cookie.
          this.auth0.parseHash({ ...leftover, hash }, finish);
          return;
        }
        finish(err, authResult);
      });
    });
  });

  get isPremium() {
    return (
      this.isSubscribed ||
      (!this.isSubscribed &&
        this.isTrial &&
        new Date().getTime() < this.premiumUntil)
    );
  }

  get franchise() {
    return this.observableFranchise.value;
  }

  get observableFranchise() {
    if (this.franchiseId) {
      return fromPromise(this.franchiseStore.getById(this.franchiseId));
    }
    return { value: undefined };
  }

  get isAuthenticated() {
    return new Date().getTime() < this.expiresAt;
  }

  resetRedirectUrl = flow(function* () {
    this.redirectUrl = "/";
  });

  renewToken(cb?: () => void) {
    if (!this.isLoggedIn) {
      return cb && cb();
    }

    // Phone app just opened: carry on with the remembered sign-in
    if (!this.accessToken) {
      const saved = restoreAppSession();
      if (saved) {
        this.setSession(saved);
        return cb && cb();
      }
    }

    this.auth0.checkSession(
      {
        audience: `https://${config.AUTH0_DOMAIN}/api/v2/`,
      },
      (err, result) => {
        if (err) {
          // this.reset()
          console.error(
            `Could not get a new token (${err.error}: ${err.error_description}).`
          );
        } else {
          this.setSession(result);
          console.log(`Successfully renewed auth!`);
        }
        return cb && cb();
      }
    );
  }

  scheduleRenewal() {
    const delay = this.expiresAt - Date.now();
    if (delay > 0) {
      this.tokenRenewalTimeout = setTimeout(() => {
        this.tokenRenewalTimeout = undefined;
        this.renewToken();
      }, delay);
    } else {
      // Check access token i local storage
      // If access token is not empty => user is logged in
      // In other case => user is logged out => do not refresh token
      if (this.isLoggedIn) {
        this.renewToken();
      }
    }
  }

  setSession(authResult, fromLogin = false) {
    if (this.tokenRenewalTimeout) {
      clearTimeout(this.tokenRenewalTimeout);
      this.tokenRenewalTimeout = undefined;
    }

    let expiresAt = authResult.expiresIn * 1000 + new Date().getTime();
    const { idTokenPayload } = authResult;
    const premiumUntil =
      idTokenPayload[`${AUTH0_NAMESPACE}premium_until`] || "0";
    const isSubscribed = !!idTokenPayload[`${AUTH0_NAMESPACE}is_subscribed`];
    const isTrial = !!idTokenPayload[`${AUTH0_NAMESPACE}is_trial`];
    const gymName = idTokenPayload[`${AUTH0_NAMESPACE}gym_name`] || "";
    const franchiseId = idTokenPayload[`${AUTH0_NAMESPACE}franchise_id`] || "";
    const disableMotivationQuotes =
      idTokenPayload[`${AUTH0_NAMESPACE}disable_motivation_quotes`] || "false";
    const isBlocked = !!idTokenPayload[`${AUTH0_NAMESPACE}is_blocked`];
    const limitFreeStation =
      !!idTokenPayload[`${AUTH0_NAMESPACE}limit_free_station`];
    const blockReason = idTokenPayload[`${AUTH0_NAMESPACE}block_reason`] || "";
    const email = idTokenPayload[`${AUTH0_NAMESPACE}email`] || "";
    const profileName = idTokenPayload.name || "";
    const emailVerified = !!idTokenPayload[`${AUTH0_NAMESPACE}email_verified`];
    const id = idTokenPayload[`${AUTH0_NAMESPACE}id`] || "";
    const gaiaToken = idTokenPayload[`${AUTH0_NAMESPACE}gaia_token`] || "";
    let explicitFilter =
      idTokenPayload[`${AUTH0_NAMESPACE}explicit_filter`] ||
      DEFAULT_EXPLICIT_FILTER;

    this.id = id;
    const loginId = idTokenPayload[`${AUTH0_NAMESPACE}login_id`] || "";
    if (loginId && (fromLogin || !this.getDeviceLoginId(id))) {
      this.setDeviceLoginId(id, loginId);
    }
    if (fromLogin) {
      this.replacedElsewhere = false;
    }
    this.gymName = gymName;
    this.gaiaToken = gaiaToken;
    this.franchiseId = franchiseId;
    this.isLoggedIn = true;
    this.email = email;
    this.name = profileName && profileName !== email ? profileName : "";
    this.isEmailVerified = emailVerified;
    this.accessToken = authResult.accessToken;
    this.idToken = authResult.idToken || "";
    this.premiumUntil = parseInt(premiumUntil, 10);
    this.expiresAt = parseInt(expiresAt, 10);
    this.isSubscribed = isSubscribed;
    this.isTrial = isTrial;
    this.isBlocked = isBlocked;
    this.limitFreeStation = limitFreeStation;
    this.blockReason = blockReason;
    this.explicitFilter = this.isPremium
      ? explicitFilter
      : DEFAULT_EXPLICIT_FILTER;

    saveAppSession(this.accessToken, this.idToken, this.expiresAt, idTokenPayload);
    this.scheduleRenewal();
  }

  /*
    Redirects 
    @TODO: move to separate module
  */
  redirectToRecurly(planId: string, newTab = true) {
    let currency = "";
    try {
      const c = (window.sessionStorage.getItem("currency") || "").toUpperCase();
      if (["USD", "GBP", "CAD", "AUD", "NZD", "ZAR"].includes(c)) currency = `?currency=${c}`;
    } catch (e) {}
    let recurlyUrl = `${config.BASE_URL_RECURLY}/subscribe/${planId}/${this.id}/${this.email}${currency}`;
    // Stripe checkout: the payment link carries the gym's login so the payment
    // is matched to the right account ('|' isn't allowed there, so it's sent as '__')
    const stripeLink = (config.STRIPE_LINKS || {})[planId];
    if (stripeLink && this.id) {
      const ref = String(this.id).replace(/\|/g, "__").replace(/[^A-Za-z0-9_-]/g, "");
      const join = stripeLink.indexOf("?") >= 0 ? "&" : "?";
      recurlyUrl = `${stripeLink}${join}client_reference_id=${encodeURIComponent(ref)}`
        + (this.email ? `&prefilled_email=${encodeURIComponent(this.email)}` : "");
    }

    if (newTab) {
      const newWindow = window.open(recurlyUrl, "_blank");
      newWindow.focus();
    } else {
      window.location.href = recurlyUrl;
    }
  }

  redirectToPricingPage(newTab = true) {
    const pricingUrl = `${config.BASE_URL_WEB}/pricing?userid=${this.id}&email=${this.email}`;
    if (newTab) {
      const newWindow = window.open(pricingUrl, "_blank");
      newWindow.focus();
    } else {
      window.location.href = pricingUrl;
    }
  }
}

decorate(UserStore, {
  isLoading: observable,
  replacedElsewhere: observable,
  accessToken: observable,
  idToken: observable,
  expiresAt: observable,
  id: observable,
  isLoggedIn: [serializable, persist, observable],
  redirectUrl: [serializable, persist, observable],
  gymName: observable,
  name: observable,
  gaiaToken: observable,
  email: observable,
  blockReason: observable,
  isEmailVerified: observable,
  isSubscribed: observable,
  isPremium: computed,
  premiumUntil: observable,
  isTrial: observable,
  isAuthenticated: computed,
  franchise: computed,
  observableFranchise: computed,
  franchiseId: observable,
  disableMotivationQuotes: observable,
  explicitFilter: observable,
  setSession: action,
});
